import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { Send, Search, User, ArrowLeft, MoreVertical, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Messages() {
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")) || null; } catch { return null; }
  });
  
  const [activeUserId, setActiveUserId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  const queryClient = useQueryClient();

  const { data: allMessages = [] } = useQuery({
    queryKey: ["messages"],
    queryFn: () => backendApi.entities.Message.list(),
    refetchInterval: 5000, // auto refresh every 5s
    enabled: !!user,
  });

  // Extract unique conversations
  const conversationsMap = new Map();
  
  allMessages.forEach(msg => {
    // If I'm the sender, the other person is the receiver
    const otherUserId = msg.sender_id === user?.id ? msg.receiver_id : msg.sender_id;
    // Guess the name
    const otherUserName = msg.sender_id === user?.id ? (msg.receiver_name || "User " + msg.receiver_id) : (msg.sender_name || "User " + msg.sender_id);
    
    if (!otherUserId) return; // safeguard

    if (!conversationsMap.has(otherUserId) || new Date(msg.created_at) > new Date(conversationsMap.get(otherUserId).lastMessageAt)) {
      conversationsMap.set(otherUserId, {
        id: otherUserId,
        name: otherUserName,
        lastMessage: msg.message || msg.content,
        lastMessageAt: msg.created_at,
        unreadCount: allMessages.filter(m => m.sender_id === otherUserId && !m.is_read).length
      });
    }
  });

  const conversations = Array.from(conversationsMap.values()).sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));

  // Determine active conversation messages
  const activeMessages = allMessages.filter(msg => 
    (msg.sender_id === user?.id && msg.receiver_id === activeUserId) ||
    (msg.sender_id === activeUserId && msg.receiver_id === user?.id)
  ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages]);

  const sendMessageMutation = useMutation({
    mutationFn: (messageText) => backendApi.entities.Message.create({
      receiver_id: activeUserId,
      message: messageText,
      content: messageText // Fallback in case backend expects content
    }),
    onSuccess: () => {
      setNewMessage("");
      queryClient.invalidateQueries(["messages"]);
    }
  });

  const handleSend = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeUserId) return;
    sendMessageMutation.mutate(newMessage);
  };

  const activeUserObj = conversations.find(c => c.id === activeUserId);

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-100px)] py-6 px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 h-full flex overflow-hidden">
        
        {/* LEFT PANEL - CONVERSATIONS */}
        <div className={`w-full md:w-80 lg:w-[350px] flex flex-col border-r border-slate-100 ${activeUserId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Messaging</h2>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search messages" className="pl-9 bg-slate-50 border-none" />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {conversations.length > 0 ? conversations.map(conv => (
              <div 
                key={conv.id} 
                onClick={() => setActiveUserId(conv.id)}
                className={`p-4 border-b border-slate-50 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${activeUserId === conv.id ? 'bg-blue-50/50 hover:bg-blue-50/50' : ''}`}
              >
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold shrink-0">
                  {conv.name.includes("User") ? <User className="w-5 h-5" /> : conv.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline mb-1">
                    <h4 className="font-semibold text-slate-900 truncate">{conv.name}</h4>
                    <span className="text-[11px] text-slate-400 shrink-0">
                      {new Date(conv.lastMessageAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <p className="text-sm text-slate-500 truncate">{conv.lastMessage}</p>
                    {conv.unreadCount > 0 && (
                      <span className="bg-[#2557a7] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0">
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full">
                <MessageSquare className="w-12 h-12 text-slate-200 mb-3" />
                <p>No messages yet</p>
                <p className="text-sm mt-1">When you connect with recruiters or employers, messages will appear here.</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL - CHAT WINDOW */}
        <div className={`flex-1 flex flex-col bg-slate-50/30 ${!activeUserId ? 'hidden md:flex' : 'flex'}`}>
          {!activeUserId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <MessageSquare className="w-16 h-16 text-slate-200 mb-4" />
              <h3 className="text-xl font-medium text-slate-600">Your Messages</h3>
              <p className="text-sm mt-2">Select a conversation to start chatting</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="h-16 px-6 border-b border-slate-100 bg-white flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <button className="md:hidden mr-2 text-slate-400 hover:text-slate-600" onClick={() => setActiveUserId(null)}>
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold shrink-0">
                    {activeUserObj?.name.includes("User") ? <User className="w-5 h-5" /> : activeUserObj?.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{activeUserObj?.name}</h3>
                  </div>
                </div>
                <button className="text-slate-400 hover:text-slate-600">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
                {activeMessages.map((msg, i) => {
                  const isSent = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id || i} className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-5 py-3 ${
                        isSent 
                          ? 'bg-[#2557a7] text-white rounded-br-sm' 
                          : 'bg-white border border-slate-100 shadow-sm text-slate-800 rounded-bl-sm'
                      }`}>
                        <p className="text-[15px] leading-relaxed break-words">{msg.message || msg.content}</p>
                        <p className={`text-[10px] mt-1 text-right ${isSent ? 'text-blue-100' : 'text-slate-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t border-slate-100">
                <form onSubmit={handleSend} className="flex gap-3">
                  <Input 
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..." 
                    className="flex-1 rounded-full bg-slate-50 border-slate-200 pl-5 h-12 focus-visible:ring-1 focus-visible:ring-[#2557a7]"
                  />
                  <Button 
                    type="submit" 
                    disabled={sendMessageMutation.isPending || !newMessage.trim()}
                    className="rounded-full w-12 h-12 p-0 bg-[#2557a7] hover:bg-[#1e4687] shrink-0 shadow-sm"
                  >
                    <Send className="w-5 h-5 ml-1" />
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
